import { COLORS } from "../../shared/constants";
import { useEffect, useState } from "react";
import * as api from "../../api";

// ─── Paired iPads Tab (Settings) ─────────────────────────────
//
// Manages the device-factor of two-tier auth. NOT to be confused with
// the existing "Devices" tab (DevicesTab.js), which is the MagPro
// hardware. This tab is for iPads/browsers paired to the clinic.
//
// Capabilities:
//   - List active devices with last-seen
//   - Show revoked devices on toggle
//   - Generate a 6-digit pair code via modal
//   - Revoke a device
//   - Surface the soft-cap warning at 25 devices

export default function IpadsTab() {
  const [devices, setDevices] = useState([]);
  const [softCap, setSoftCap] = useState(25);
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Pair-code modal state
  const [pairOpen, setPairOpen] = useState(false);
  const [pairName, setPairName] = useState("");
  const [pairCode, setPairCode] = useState(null); // {code, expires_at, ttl_minutes}
  const [pairForce, setPairForce] = useState(false);
  const [pairError, setPairError] = useState("");
  const [pairBusy, setPairBusy] = useState(false);
  const [pairCountdown, setPairCountdown] = useState(0);

  const loadDevices = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.deviceList(includeRevoked);
      setDevices((data && data.devices) || []);
      if (data && data.soft_cap) setSoftCap(data.soft_cap);
    } catch (e) {
      setError(e.error || e.detail || "Failed to load devices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDevices();
  }, [includeRevoked]);

  // ── Countdown timer for the visible pair code ──
  useEffect(() => {
    if (!pairCode || !pairCode.expires_at) {
      setPairCountdown(0);
      return;
    }
    const tick = () => {
      const ms = new Date(pairCode.expires_at) - new Date();
      setPairCountdown(Math.max(0, Math.floor(ms / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [pairCode]);

  // ── Pair-code modal handlers ──
  const openPair = () => {
    setPairOpen(true);
    setPairName("");
    setPairCode(null);
    setPairForce(false);
    setPairError("");
    setPairBusy(false);
  };

  const closePair = () => {
    setPairOpen(false);
    if (pairCode) {
      // Refresh list — the new device only shows up after pair-complete,
      // not pair-init, but list refresh is harmless.
      loadDevices();
    }
  };

  const submitPair = async (force = false) => {
    if (!pairName.trim()) {
      setPairError("Display name is required");
      return;
    }
    setPairBusy(true);
    setPairError("");
    try {
      const data = await api.devicePairInit(pairName.trim(), force);
      setPairCode(data);
    } catch (e) {
      // 409 soft_cap_reached → offer override
      if (e && e.status === 409 && e.error === "soft_cap_reached") {
        setPairError(
          `${e.detail} Click "Override soft cap" to proceed anyway.`
        );
        setPairForce(true);
      } else if (e && e.status === 429) {
        setPairError(
          e.detail || "Too many pair codes generated recently. Wait a bit."
        );
      } else {
        setPairError(e.error || e.detail || "Failed to generate pair code");
      }
    } finally {
      setPairBusy(false);
    }
  };

  // ── Revoke ──
  const handleRevoke = async (device) => {
    const reason = window.prompt(
      `Revoke "${device.display_name}"? This will lock out the iPad immediately.\n\n` +
        "Optional reason (shown in audit log):",
      ""
    );
    if (reason === null) return; // user cancelled
    try {
      await api.deviceRevoke(device.id, reason || "");
      await loadDevices();
    } catch (e) {
      window.alert(e.error || e.detail || "Failed to revoke device");
    }
  };

  // ── Render helpers ──
  const formatRelative = (iso) => {
    if (!iso) return "never";
    const ms = new Date() - new Date(iso);
    const min = Math.floor(ms / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min} min ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} hr ago`;
    const days = Math.floor(hr / 24);
    if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
    return new Date(iso).toLocaleDateString();
  };

  const formatCountdown = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const activeCount = devices.filter((d) => d.is_active).length;
  const nearCap = activeCount >= softCap;

  // ─────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header + action button */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: "16px",
      }}>
        <h3 style={{ margin: 0, color: COLORS.text, fontSize: "16px", fontWeight: "600" }}>
          Paired iPads
        </h3>
        <button
          onClick={openPair}
          style={{
            background: COLORS.primary, color: "#FFFFFF", border: "none",
            borderRadius: "8px", padding: "8px 16px", fontSize: "13px",
            fontWeight: "600", cursor: "pointer",
          }}
        >
          + Pair new iPad
        </button>
      </div>

      {nearCap && (
        <div style={{
          background: "#FEF3C7", color: "#92400E",
          padding: "10px 14px", borderRadius: "8px", marginBottom: "12px",
          fontSize: "13px",
        }}>
          ⚠ This clinic has {activeCount} of {softCap} active devices — soft cap reached.
          New pairings will require an override.
        </div>
      )}

      <label style={{
        display: "inline-flex", alignItems: "center", gap: "6px",
        fontSize: "12px", color: COLORS.textSecondary, marginBottom: "12px",
        cursor: "pointer",
      }}>
        <input
          type="checkbox"
          checked={includeRevoked}
          onChange={(e) => setIncludeRevoked(e.target.checked)}
        />
        Show revoked devices
      </label>

      {error && (
        <p style={{ color: COLORS.danger, fontSize: "13px" }}>{error}</p>
      )}

      {loading ? (
        <p style={{ color: COLORS.textSecondary, fontSize: "13px" }}>Loading…</p>
      ) : devices.length === 0 ? (
        <p style={{ color: COLORS.textSecondary, fontSize: "13px" }}>
          No iPads paired yet. Click "Pair new iPad" to enroll one.
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {devices.map((d) => (
            <div
              key={d.id}
              style={{
                background: d.is_active ? COLORS.surface : "#F3F4F6",
                border: `1px solid ${COLORS.border}`,
                borderRadius: "8px",
                padding: "12px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                opacity: d.is_active ? 1 : 0.6,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: "14px", fontWeight: "600", color: COLORS.text }}>
                  {d.display_name}
                  {!d.is_active && (
                    <span style={{
                      marginLeft: "8px",
                      fontSize: "11px",
                      color: COLORS.danger,
                      fontWeight: "500",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                    }}>
                      revoked
                    </span>
                  )}
                </div>
                <div style={{ fontSize: "12px", color: COLORS.textSecondary, marginTop: "4px" }}>
                  Last seen: {formatRelative(d.last_seen_at)}
                  {" · "}
                  Paired: {formatRelative(d.paired_at)}
                  {d.paired_by_name && ` (by ${d.paired_by_name})`}
                </div>
                {!d.is_active && d.revoke_reason && (
                  <div style={{ fontSize: "12px", color: COLORS.textSecondary, marginTop: "2px", fontStyle: "italic" }}>
                    Reason: {d.revoke_reason}
                  </div>
                )}
              </div>
              {d.is_active && (
                <button
                  onClick={() => handleRevoke(d)}
                  style={{
                    background: "transparent",
                    color: COLORS.danger,
                    border: `1px solid ${COLORS.danger}`,
                    borderRadius: "6px",
                    padding: "6px 12px",
                    fontSize: "12px",
                    fontWeight: "500",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  Revoke
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Pair modal ────────────────────────────────────── */}
      {pairOpen && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 1000, padding: "20px",
        }}>
          <div style={{
            background: COLORS.surface, borderRadius: "12px",
            padding: "28px", width: "100%", maxWidth: "420px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: "16px", fontWeight: "600", color: COLORS.text }}>
              {pairCode ? "Pair code generated" : "Pair new iPad"}
            </h3>

            {!pairCode ? (
              <>
                <p style={{ fontSize: "13px", color: COLORS.textSecondary, marginBottom: "12px" }}>
                  Give this iPad a name so you can identify it later
                  ("Treatment Room A iPad," "Front Desk," etc.).
                </p>
                <input
                  type="text"
                  value={pairName}
                  onChange={(e) => setPairName(e.target.value)}
                  placeholder="Display name"
                  autoFocus
                  style={{
                    width: "100%", padding: "10px 12px", fontSize: "14px",
                    border: `1px solid ${COLORS.border}`, borderRadius: "8px",
                    marginBottom: "12px", boxSizing: "border-box",
                  }}
                />
                {pairError && (
                  <p style={{ color: COLORS.danger, fontSize: "12px", marginBottom: "12px" }}>
                    {pairError}
                  </p>
                )}
                <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                  <button
                    onClick={closePair}
                    disabled={pairBusy}
                    style={{
                      background: "transparent", color: COLORS.textSecondary,
                      border: `1px solid ${COLORS.border}`, borderRadius: "6px",
                      padding: "8px 14px", fontSize: "13px", cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => submitPair(pairForce)}
                    disabled={pairBusy}
                    style={{
                      background: COLORS.primary, color: "#FFFFFF", border: "none",
                      borderRadius: "6px", padding: "8px 14px", fontSize: "13px",
                      fontWeight: "600", cursor: pairBusy ? "wait" : "pointer",
                      opacity: pairBusy ? 0.6 : 1,
                    }}
                  >
                    {pairBusy
                      ? "Generating…"
                      : pairForce
                      ? "Override soft cap"
                      : "Generate code"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p style={{ fontSize: "13px", color: COLORS.textSecondary, marginBottom: "16px" }}>
                  Type this code into the new iPad's pair screen.
                </p>
                <div style={{
                  background: COLORS.primaryLight, color: COLORS.primary,
                  fontSize: "44px", fontWeight: "700", letterSpacing: "8px",
                  textAlign: "center", padding: "24px", borderRadius: "10px",
                  marginBottom: "12px", fontFamily: "monospace",
                }}>
                  {pairCode.code}
                </div>
                <p style={{ fontSize: "12px", color: COLORS.textSecondary, textAlign: "center", marginBottom: "16px" }}>
                  Expires in {formatCountdown(pairCountdown)} · single-use
                </p>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button
                    onClick={closePair}
                    style={{
                      background: COLORS.primary, color: "#FFFFFF", border: "none",
                      borderRadius: "6px", padding: "8px 14px", fontSize: "13px",
                      fontWeight: "600", cursor: "pointer",
                    }}
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
